import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  alpha,
  useTheme,
  Chip,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Divider,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Code as CodeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import jobseekerapi from '../../../services/jobseekerApi';

// Types
interface Education {
  degree?: string;
  institution?: string;
  year?: string;
  [key: string]: any;
}

interface Experience {
  company?: string;
  position?: string;
  duration?: string;
  [key: string]: any;
}

interface JobSeeker {
  _id: string;
  job_seeker_code: string;
  first_name: string;
  last_name: string;
  country_code: number;
  email_id: string;
  reference: string;
  resume: string | null;
  privacy_policy_accept: boolean;
  profile_image: string | null;
  skills: string[];
  stripe_customer_id: string | null;
  is_email_verified: boolean;
  is_process_ongoing: boolean;
  total_ongoing_application: number;
  ongoing_application: number;
  is_active: boolean;
  is_user_login: boolean;
  is_deleted: boolean;
  education: Education[];
  experience: Experience[];
  createdAt: string;
  updatedAt: string;
  __v: number;
  [key: string]: any;
}

const Candidates: React.FC = () => {
  const theme = useTheme();

  // State variables
  const [jobSeekers, setJobSeekers] = useState<JobSeeker[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedJobSeeker, setSelectedJobSeeker] = useState<JobSeeker | null>(null);

  // Fetch job seekers on component mount and when filters/pagination changes
  useEffect(() => {
    fetchJobSeekers();
  }, [page, rowsPerPage, searchQuery]);

  // Function to fetch job seekers
  const fetchJobSeekers = async () => {
    setLoading(true);
    setError(null);

    try {
      // startToken is page number (1-indexed), endToken is number of records per page
      const startToken = page + 1;
      const endToken = rowsPerPage;

      const response = await jobseekerapi.post('/jobseeker/jobseekerList', {
        startToken,
        endToken,
        search: searchQuery,
      });

      if (response?.data?.status) {
        setJobSeekers(response.data.data || []);
        setTotalRows(response.data.record_count || 0);
      } else {
        setError('Failed to fetch job seekers');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to fetch job seekers');
      console.error('Error fetching job seekers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Reset search
  const resetFilters = () => {
    setSearchQuery('');
    setPage(0);
  };

  // Handle modal open
  const handleViewDetails = (jobSeeker: JobSeeker) => {
    setSelectedJobSeeker(jobSeeker);
    setOpenModal(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedJobSeeker(null);
  };

  // Render status chip based on is_active and is_email_verified
  const renderStatusChip = (jobSeeker: JobSeeker) => {
    if (!jobSeeker.is_active) {
      return <Chip label="Inactive" color="error" size="small" />;
    }
    if (!jobSeeker.is_email_verified) {
      return <Chip label="Unverified" color="warning" size="small" />;
    }
    return <Chip label="Active" color="success" size="small" />;
  };

  // Format date
  const formatDate = (date: string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            mb: 4,
            fontWeight: 700,
            color: theme.palette.primary.main,
            position: 'relative',
            display: 'inline-block',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 40,
              height: 4,
              backgroundColor: theme.palette.secondary.main,
              borderRadius: 2,
            },
          }}
        >
          Job Seekers
        </Typography>
      </Box>

      {/* Search Filter */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
        }}
      >
        <TextField
          label="Search Job Seekers"
          variant="outlined"
          size="small"
          fullWidth
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search by name, email, or phone"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={resetFilters}>
          Reset
        </Button>
      </Paper>

      {/* Job Seekers Table */}
      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Job Seeker Code</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Registered On</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'error.main' }}>
                    {error}
                  </TableCell>
                </TableRow>
              ) : jobSeekers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    {searchQuery ? 'No job seekers found matching your search' : 'No job seekers found'}
                  </TableCell>
                </TableRow>
              ) : (
                jobSeekers.map((jobSeeker) => {
                  const candidateName = `${jobSeeker?.first_name || ''} ${jobSeeker?.last_name || ''}`; 
                  return (
                  <TableRow key={jobSeeker._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={jobSeeker.profile_image || undefined}
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: theme.palette.primary.main,
                            fontSize: '0.875rem',
                          }}
                        >
                          {jobSeeker.first_name ? jobSeeker.first_name.charAt(0).toUpperCase() : <PersonIcon fontSize="small" />}
                        </Avatar>
                        <Typography sx={{ fontWeight: 500 }}>
                          {candidateName || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {jobSeeker.email_id || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={jobSeeker.job_seeker_code || 'N/A'}
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily: 'monospace' }}
                      />
                    </TableCell>
                    <TableCell>
                      {renderStatusChip(jobSeeker)}
                    </TableCell>
                    <TableCell>
                      {formatDate(jobSeeker.createdAt)}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleViewDetails(jobSeeker)}
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          },
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                       <IconButton
                        color="secondary"
                        size="small"
                        title="Auto Login"
                        onClick={() => window.location.host === "admin.jobla.ai" ? 
                          window.open(`https://jobseeker.jobla.ai/autologin/${jobSeeker._id}`, '_blank')
                        :
                          window.open(`https://beta.jobseeker.jobla.ai/autologin/${jobSeeker._id}`, '_blank')
                        }
                        // onClick={() => window.open(`http://localhost:5173/autologin/${jobSeeker._id}`, '_blank')}
                        sx={{
                          mr: 1,
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                          },
                        }}
                      >
                        <LoginIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )})
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Details Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Job Seeker Details
          </Typography>
          <IconButton onClick={handleCloseModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {selectedJobSeeker && (
            <Box>
              {/* Profile Section */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                <Avatar
                  src={selectedJobSeeker.profile_image || undefined}
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: theme.palette.primary.main,
                    fontSize: '2rem',
                  }}
                >
                  {selectedJobSeeker.first_name?.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {selectedJobSeeker.first_name} {selectedJobSeeker.last_name}
                  </Typography>
                  <Chip
                    label={selectedJobSeeker.job_seeker_code}
                    size="small"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace' }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Contact Information */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Contact Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <EmailIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body2">{selectedJobSeeker.email_id}</Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PhoneIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Country Code
                      </Typography>
                      <Typography variant="body2">+{selectedJobSeeker.country_code}</Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Account Status */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Account Status
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {selectedJobSeeker.is_active ? (
                      <CheckCircleIcon fontSize="small" color="success" />
                    ) : (
                      <CancelIcon fontSize="small" color="error" />
                    )}
                    <Typography variant="body2">
                      {selectedJobSeeker.is_active ? 'Active' : 'Inactive'}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {selectedJobSeeker.is_email_verified ? (
                      <CheckCircleIcon fontSize="small" color="success" />
                    ) : (
                      <CancelIcon fontSize="small" color="error" />
                    )}
                    <Typography variant="body2">
                      Email {selectedJobSeeker.is_email_verified ? 'Verified' : 'Not Verified'}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {selectedJobSeeker.is_user_login ? (
                      <CheckCircleIcon fontSize="small" color="success" />
                    ) : (
                      <CancelIcon fontSize="small" color="error" />
                    )}
                    <Typography variant="body2">
                      User {selectedJobSeeker.is_user_login ? 'Logged In' : 'Not Logged In'}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {selectedJobSeeker.privacy_policy_accept ? (
                      <CheckCircleIcon fontSize="small" color="success" />
                    ) : (
                      <CancelIcon fontSize="small" color="error" />
                    )}
                    <Typography variant="body2">
                      Privacy Policy {selectedJobSeeker.privacy_policy_accept ? 'Accepted' : 'Not Accepted'}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Application Information */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Application Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Ongoing Applications
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedJobSeeker.ongoing_application}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Total Ongoing Applications
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedJobSeeker.total_ongoing_application}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {selectedJobSeeker.is_process_ongoing ? (
                      <CheckCircleIcon fontSize="small" color="success" />
                    ) : (
                      <CancelIcon fontSize="small" color="error" />
                    )}
                    <Typography variant="body2">
                      Process {selectedJobSeeker.is_process_ongoing ? 'Ongoing' : 'Not Ongoing'}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Skills */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Skills
              </Typography>
              <Box sx={{ mb: 3 }}>
                {selectedJobSeeker.skills && selectedJobSeeker.skills.length > 0 ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {selectedJobSeeker.skills.map((skill, index) => (
                      <Chip key={index} label={skill} size="small" color="primary" variant="outlined" />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No skills added
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Education */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                <SchoolIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Education
              </Typography>
              <Box sx={{ mb: 3 }}>
                {selectedJobSeeker.education && selectedJobSeeker.education.length > 0 ? (
                  selectedJobSeeker.education.map((edu, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        p: 2,
                        mb: 1,
                        backgroundColor: alpha(theme.palette.primary.main, 0.02),
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {edu.degree || 'Degree not specified'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {edu.institution || 'Institution not specified'}
                      </Typography>
                      {edu.year && (
                        <Typography variant="caption" color="text.secondary">
                          {edu.year}
                        </Typography>
                      )}
                    </Paper>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No education details added
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Experience */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                <WorkIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Experience
              </Typography>
              <Box sx={{ mb: 3 }}>
                {selectedJobSeeker.experience && selectedJobSeeker.experience.length > 0 ? (
                  selectedJobSeeker.experience.map((exp, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        p: 2,
                        mb: 1,
                        backgroundColor: alpha(theme.palette.primary.main, 0.02),
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {exp.position || 'Position not specified'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {exp.company || 'Company not specified'}
                      </Typography>
                      {exp.duration && (
                        <Typography variant="caption" color="text.secondary">
                          {exp.duration}
                        </Typography>
                      )}
                    </Paper>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No experience details added
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Additional Information */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Additional Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Reference
                  </Typography>
                  <Typography variant="body2">{selectedJobSeeker.reference || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Resume
                  </Typography>
                  <Typography variant="body2">
                    {selectedJobSeeker.resume ? (
                      <a
                        href={selectedJobSeeker.resume}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: theme.palette.primary.main }}
                      >
                        View Resume
                      </a>
                    ) : (
                      'Not uploaded'
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Registered On
                  </Typography>
                  <Typography variant="body2">{formatDate(selectedJobSeeker.createdAt)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body2">{formatDate(selectedJobSeeker.updatedAt)}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={handleCloseModal} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Candidates;
